<template>
  <div id="order-details-purchase" class="fill-height align-start">
    <v-dialog
      v-model="cancelConfirmationDialog"
      content-class="i-dialog"
      width="400"
      persistent
      @click:outside="cancelConfirmationDialog = false"
      @keydown.esc="cancelConfirmationDialog = false"
    >
      <v-card>
        <template>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Cancel Order</span>
          </v-card-title>

          <v-card-text class="py-4 text-center">
            <span><strong>Specify reason for cancellation</strong></span>
            <br />
            <v-text-field
              append-icon="mdi-message-reply-text"
              class="pa-0 mt-2 mb-n1"
              counter="255"
              maxlength="255"
              dense
              label="Reason for cancellation"
              outlined
              required
              v-model="reasonForCancellation"
            ></v-text-field>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              rounded
              text
              class="i-btn-text-black"
              @click="cancelConfirmationDialog = false"
            >
              Close
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary"
              @click="cancelOrder"
              :disabled="!reasonForCancellation"
              :loading="cancelling"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deliveryDialog"
      content-class="i-dialog"
      width="500"
      persistent
      @click:outside="
        ((deliveryDialog = false), (deliveryDate = trackingLink = null))
      "
      @keydown.esc="
        ((deliveryDialog = false), (deliveryDate = trackingLink = null))
      "
    >
      <v-card>
        <template>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Despatch Information</span>
          </v-card-title>

          <v-card-text
            class="py-4"
            :class="{
              'i-dialog-content-div-large': $vuetifyDisplay.mdAndUp
            }"
          >
            <v-row>
              <v-col cols="12">
                <p class="mb-2 g-bold">
                  {{ isILR ? 'CUSTOMER' : 'RETAILER' }} MESSAGE
                </p>
                <p class="i-message">{{ retailerMessage }}</p>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <p class="mb-2 g-bold">
                  ESTIMATED DELIVERY DATE
                  <template v-if="!isServiceOrder">(REQUIRED)</template>
                  <template v-else>(OPTIONAL)</template>
                </p>
                <v-menu
                  v-model="deliveryDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="15"
                  :nudge-top="30"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      append-icon="mdi-calendar"
                      autofocus
                      background-color="#0000000f"
                      dense
                      :label="
                        isServiceOrder
                          ? 'Estimated Delivery Date (Optional)'
                          : 'Estimated Delivery Date *'
                      "
                      outlined
                      readonly
                      :rules="
                        isServiceOrder
                          ? []
                          : [(v) => !!v || 'Estimated Delivery Date required']
                      "
                      v-on="on"
                      v-model="formattedDeliveryDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :min="currentDate"
                    v-model="deliveryDate"
                    @change="deliveryDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" class="mt-n4">
                <p class="mb-2 g-bold">TRACKING LINK (OPTIONAL)</p>
                <v-text-field
                  append-icon="mdi-map-marker"
                  background-color="#0000000f"
                  dense
                  label="Tracking Link (Optional)"
                  outlined
                  :rules="trackingLinkRules"
                  v-model="trackingLink"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="mt-n2 mb-6"></v-divider>

            <div class="my-4 text-center">
              <template v-if="mutableOrder.paymentType === 'SUPPLIER_DIRECT'">
                <span class="font-weight-bold"
                  >If Prepayment is required, once Payment from
                  {{ isILR ? 'Customer' : 'Retailer' }} is received, goods can
                  be dispatched</span
                ><br /><br />
              </template>

              <span>Once confirmed, order will move to</span><br />
              <span><strong>Delivered</strong> for delivery confirmation.</span
              ><br />
              <span>Do you want to confirm now?</span>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              text
              rounded
              class="i-btn-text-secondary mr-2"
              @click="
                ((deliveryDialog = false), (deliveryDate = trackingLink = null))
              "
            >
              Cancel
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary mr-2"
              :loading="submitting"
              :disabled="!validDespatchInfo"
              @click="despatch()"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="unreadDeliveryChatDialog"
      content-class="i-dialog"
      width="460"
      persistent
      @click:outside="unreadDeliveryChatDialog = false"
      @keydown.esc="unreadDeliveryChatDialog = false"
    >
      <v-card>
        <template>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Unread Message</span>
          </v-card-title>

          <v-card-text class="py-4">
            <v-alert type="warning" outlined class="mb-0">
              Please read the unread retailer message before confirming
              delivery. Once the message has been read, you can add the
              delivered date and confirm delivery.
            </v-alert>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              text
              rounded
              class="i-btn-text-secondary mr-2"
              @click="unreadDeliveryChatDialog = false"
            >
              Cancel
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary mr-2"
              @click="readUnreadDeliveryMessage"
            >
              Read Message
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deliveredDialog"
      content-class="i-dialog"
      width="500"
      persistent
      @click:outside="
        ((deliveredDialog = proofOfDelivery = false), (deliveredDate = null))
      "
      @keydown.esc="
        ((deliveredDialog = proofOfDelivery = false), (deliveredDate = null))
      "
    >
      <v-card>
        <template>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Delivery Information</span>
          </v-card-title>

          <v-card-text
            class="py-4"
            :class="{
              'i-dialog-content-div-large': $vuetifyDisplay.mdAndUp
            }"
          >
            <v-row>
              <v-col cols="12">
                <p class="mb-2 g-bold">
                  {{ isILR ? 'CUSTOMER' : 'RETAILER' }} MESSAGE
                </p>
                <p class="i-message">{{ retailerMessage }}</p>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <p class="mb-2 g-bold">DELIVERED DATE (REQUIRED)</p>
                <v-menu
                  v-model="deliveredDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="15"
                  :nudge-top="30"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      append-icon="mdi-calendar"
                      autofocus
                      background-color="#0000000f"
                      dense
                      label="Delivered Date *"
                      outlined
                      readonly
                      :rules="[(v) => !!v || 'Delivered Date required']"
                      v-on="on"
                      v-model="formattedDeliveredDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :min="
                      dateToISOStringIncludingTimezone(mutableOrder.createdAt)
                    "
                    :max="currentDate"
                    v-model="deliveredDate"
                    @change="deliveredDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" class="mt-n6 mb-2" v-if="!isServiceOrder">
                <p class="mb-2 g-bold">PROOF OF DELIVERY (REQUIRED)</p>
                <v-checkbox
                  required
                  class="checkbox"
                  label="Proof of Delivery will be available"
                  v-model="proofOfDelivery"
                  :rules="[(v) => !!v || 'You must accept to confirm Delivery']"
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-divider class="mt-n2 mb-6"></v-divider>

            <div class="my-4 text-center">
              <span
                >Once Delivered Date is added this PO will move to Purchase
                Order History in Reports. Do you want to confirm now?</span
              >
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              text
              rounded
              class="i-btn-text-secondary mr-2"
              @click="
                ((deliveredDialog = proofOfDelivery = false),
                (deliveredDate = null))
              "
            >
              Cancel
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary mr-2"
              :loading="submitting || checkingUnreadDeliveryMessages"
              :disabled="
                !deliveredDate || (!isServiceOrder && !proofOfDelivery)
              "
              @click="deliver()"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-row class="g-container" no-gutters>
      <v-col>
        <i-breadcrumbs :breadcrumbs="computedBreadcrumbs"></i-breadcrumbs>
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-col class="mt-2 pa-0">
            <v-card class="rounded-2 mt-4 mb-6">
              <v-row no-gutters align="center" class="p-div-title py-3 px-3">
                <v-col cols="12">
                  <v-chip v-if="isCancelled" small class="mb-2 i-tag-warning"
                    ><strong>{{ mutableOrder.status }}</strong></v-chip
                  >
                  <v-chip
                    v-else-if="isProcessedOffline"
                    small
                    class="mb-2 i-tag-primary"
                    ><strong>OFFLINE</strong></v-chip
                  >
                  <p class="g-heading-m g-bold p-title mr-2">
                    Purchase Order Number
                  </p>
                  <p class="g-heading-m g-bold p-title mr-2">
                    {{ `#${mutableOrder.supplierOrderNumber}` }}
                  </p>
                </v-col>
              </v-row>

              <div class="pa-3">
                <v-row no-gutters>
                  <v-col cols="6" class="py-1">
                    <span>ORDER DATE & TIME</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDateTime12h(mutableOrder.createdAt)
                    }}</span>
                  </v-col>
                  <v-col
                    v-if="mutableOrder.actualFinalConfirmationDate"
                    cols="6"
                    class="py-1"
                  >
                    <span>PO CONFIRMED DATE</span>
                  </v-col>
                  <v-col
                    v-if="mutableOrder.actualFinalConfirmationDate"
                    cols="6"
                    class="py-1 pl-4"
                  >
                    <span>{{
                      $filters.formatDateTime12h(
                        mutableOrder.actualFinalConfirmationDate
                      )
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>PO TOTAL{{ incTaxSuffixUpper }}</span>
                  </v-col>
                  <v-col v-if="mutableOrder.summary" cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.currency(displayOrderTotal(mutableOrder.summary))
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>PAYMENT TYPE</span>
                  </v-col>
                  <v-col
                    cols="6"
                    class="py-1 pl-4"
                    :class="{
                      'font-weight-bold':
                        mutableOrder.paymentType === 'SUPPLIER_DIRECT'
                    }"
                  >
                    <i-payment-type
                      v-if="mutableOrder.paymentType"
                      :paymentType="mutableOrder.paymentType"
                      :textOnly="true"
                    />
                    <span
                      v-if="mutableOrder.paymentType === 'SUPPLIER_DIRECT'"
                      class="error--text text--darken-1"
                    >
                      <template v-if="mutableOrder.offlinePaymentDays">
                        ({{ mutableOrder.offlinePaymentDays }} days)
                      </template>
                      <template v-else> (Prepayment) </template>
                    </span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>PROCESSED BY</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4 text-break">
                    {{ processedByDisplay }}
                  </v-col>
                </v-row>

                <v-divider class="mt-3 mb-2"></v-divider>

                <v-row no-gutters>
                  <template v-if="hasBannerGroup">
                    <v-col cols="6" class="py-1">
                      <span>BANNER GROUP</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ mutableOrder.store.bannerGroupName }}</span>
                    </v-col>
                  </template>
                  <v-col cols="6" class="py-1">
                    <span>{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ showStoreName }}</span>
                    <v-btn
                      dense
                      v-if="mutableOrder.supplierOrderQueryId"
                      icon
                      small
                      v-tooltip="{
                        content: 'Order Query',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      :to="{
                        name: 'order-query-details',
                        params: { id: mutableOrder.supplierOrderQueryId }
                      }"
                    >
                      <v-icon small class="i-order-query-link"
                        >mdi-clock-alert-outline</v-icon
                      >
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>DELIVERY ADDRESS</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ showDeliveryAddress }}</span>
                  </v-col>
                </v-row>

                <v-divider class="mt-3 mb-2"></v-divider>

                <div class="py-2">
                  <strong>{{ isILR ? 'CUSTOMER' : 'RETAILER' }} MESSAGE</strong>
                  <div class="i-message">
                    <span>{{ retailerMessage }}</span>
                  </div>
                </div>

                <div class="py-2" v-if="supplierMessages">
                  <strong>SUPPLIER MESSAGE(S)</strong>
                  <div class="i-message">
                    <span
                      v-for="(supplierMessage, index) in supplierMessages"
                      :key="index"
                      >{{ supplierMessage }}<br
                    /></span>
                  </div>
                </div>

                <v-divider
                  v-if="
                    !isServiceOrder &&
                    isDespatched &&
                    !isCancelled &&
                    !isProcessedOffline
                  "
                  class="mt-3 mb-4"
                ></v-divider>

                <template
                  v-if="
                    !isServiceOrder &&
                    isDespatched &&
                    !isCancelled &&
                    !isProcessedOffline
                  "
                >
                  <v-row>
                    <v-col cols="12">
                      <strong>PROVIDED INFORMATION</strong>
                    </v-col>

                    <v-col cols="12" sm="6" class="py-0" v-if="deliveryDate">
                      <v-text-field
                        dense
                        outlined
                        readonly
                        label="Estimated Delivery Date"
                        background-color="#0000000f"
                        append-icon="mdi-calendar"
                        v-model="formattedDeliveryDate"
                      ></v-text-field>
                    </v-col>

                    <v-col v-if="isDelivered" cols="12" sm="6" class="py-0">
                      <v-text-field
                        dense
                        outlined
                        readonly
                        label="Delivered Date"
                        background-color="#0000000f"
                        append-icon="mdi-calendar"
                        v-model="formattedDeliveredDate"
                      ></v-text-field>
                    </v-col>

                    <v-col
                      v-if="mutableOrder.retailerAcceptedDate"
                      cols="12"
                      sm="6"
                      class="py-0"
                    >
                      <v-text-field
                        dense
                        outlined
                        readonly
                        :label="
                          tenantPaymentTransactionsConfig.purchaseOrderFinalisedDateField ===
                          'SUPPLIER_DELIVERED_ENTRY_DATE'
                            ? `${
                                isILR ? 'Customer' : 'Retailer'
                              } Actions Closed Date`
                            : `${isILR ? 'Customer' : 'Retailer'} Accepted Date`
                        "
                        background-color="#0000000f"
                        append-icon="mdi-calendar"
                        v-model="formattedRetailerAcceptedDate"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        dense
                        outlined
                        readonly
                        label="Tracking Link"
                        background-color="#0000000f"
                        append-icon="mdi-map-marker"
                        :value="trackingLink || 'Not provided'"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>

                <v-divider class="mt-0 mb-2"></v-divider>

                <v-row class="mx-1 my-3" v-if="!isDelivered">
                  <v-btn
                    rounded
                    text
                    class="i-btn-text-red g-full-width mb-3"
                    @click="cancelConfirmationDialog = true"
                    v-if="showCancelOrderButton"
                  >
                    Cancel Order
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-orange g-full-width"
                    :disabled="isServiceOrder && !allServiceEtaDatesFilled"
                    @click="deliveryDialog = true"
                    v-if="!isDespatched && !isServiceOrder"
                  >
                    Confirm Despatch
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-orange g-full-width"
                    :disabled="!allServiceEtaDatesFilled"
                    :loading="submitting"
                    @click="saveServiceEtaDates()"
                    v-if="showSaveServiceEtaButton"
                  >
                    Save ETA Date
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-orange g-full-width"
                    :loading="checkingUnreadDeliveryMessages"
                    @click="openDeliveredDialog"
                    v-if="isDespatched && !isServiceOrder"
                  >
                    Confirm Delivery
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-orange g-full-width mb-3"
                    :disabled="!hasAnyServiceCompletedDatesFilled"
                    :loading="submitting"
                    @click="saveCompletedServiceDates()"
                    v-if="showSaveCompletedServiceDateButton"
                  >
                    Save Completed Service Date
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-orange g-full-width"
                    :disabled="!allServiceCompletedDatesFilled"
                    :loading="submitting"
                    @click="confirmServicesComplete()"
                    v-if="showConfirmServicesCompleteButton"
                  >
                    Confirm Services Complete
                  </v-btn>
                </v-row>

                <div v-if="!isCancelled" class="my-3">
                  <v-btn
                    class="g-full-width i-btn-block-primary"
                    :disabled="!mutableOrder.confirmedPdfAvailable"
                    @click="fetchPDF()"
                  >
                    <span>Export Purchase Order</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>

                <div v-if="!isServiceOrder && !isCancelled" class="my-3">
                  <v-btn
                    class="g-full-width i-btn-block-primary"
                    :disabled="!mutableOrder.confirmedPdfAvailable"
                    @click="fetchPDF(true)"
                  >
                    <span>Export Delivery Note</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>

                <div v-if="!isCancelled && !isProcessedOffline" class="my-3">
                  <v-btn
                    class="g-full-width i-btn-block-primary"
                    :disabled="!mutableOrder.invoiceNumber"
                    @click="fetchInvoicePDF()"
                  >
                    <span>Export Fees Invoice</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card>

            <v-card
              v-for="item in mutableOrder.supplierOrderItems"
              :key="item.id"
              class="rounded-2 pa-4 pa-md-6 mb-6"
            >
              <v-row no-gutters>
                <v-col cols="6" class="py-1">
                  <span
                    ><strong>{{ headers[0].text }}</strong></span
                  >
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span
                    ><strong>{{
                      item.supplierProductCode || 'Not available'
                    }}</strong></span
                  >
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[1].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.productDescription || 'Not available' }}</span>
                </v-col>
                <v-col v-if="!isServiceOrder" cols="6" class="py-1">
                  <span>{{ headers[2].text }}</span>
                </v-col>
                <v-col v-if="!isServiceOrder" cols="6" class="py-1 pl-4">
                  <span>{{ item.gtin || 'Not available' }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>
                    {{ isServiceOrder ? headers[2].text : headers[3].text }}
                  </span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ showItemPrice(item) }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>
                    {{ isServiceOrder ? headers[3].text : headers[4].text }}
                  </span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ $filters.text(item.originalQuantity) }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>
                    {{ isServiceOrder ? headers[4].text : headers[5].text }}
                  </span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ $filters.text(item.quantity) }}</span>
                </v-col>
                <template v-if="item.weightType === 'VARIABLE_WEIGHT'">
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[6].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span v-if="item.originalVariableWeightInKg">{{
                      `${item.originalVariableWeightInKg} KG`
                    }}</span>
                    <span v-else>-</span>
                  </v-col>
                </template>
                <template v-if="item.weightType === 'VARIABLE_WEIGHT'">
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[7].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span v-if="item.variableWeightInKg">{{
                      `${item.variableWeightInKg} KG`
                    }}</span>
                    <span v-else>-</span>
                  </v-col>
                </template>
                <template v-if="isServiceOrder">
                  <v-col cols="6" class="py-1">
                    <span>REQUESTED DATE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDate(item.retailerRequestedDate)
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>ETA DATE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <v-menu
                      v-if="showServiceEtaEditor(item)"
                      v-model="serviceEtaMenus[serviceEtaInputKey(item)]"
                      :close-on-content-click="false"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-bind="attrs"
                          append-icon="mdi-calendar"
                          background-color="#0000000f"
                          dense
                          hide-details
                          :class="{
                            'p-service-date-required':
                              isServiceEtaRequiredForItem(item) &&
                              !item.supplierEtaDate
                          }"
                          outlined
                          readonly
                          :value="
                            $filters.formatDatePicker(item.supplierEtaDate)
                          "
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        :min="currentDate"
                        v-model="item.supplierEtaDate"
                        @change="onPickServiceEtaDate(item)"
                      ></v-date-picker>
                    </v-menu>
                    <span v-else>{{ serviceEtaDisplayValue(item) }}</span>
                  </v-col>
                  <template v-if="isDespatched">
                    <v-col cols="6" class="py-1">
                      <span>COMPLETED SERVICE DATE</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <div
                        v-if="showServiceCompletedActions(item)"
                        class="d-flex align-center ga-1"
                      >
                        <span>{{ serviceCompletedDisplayValue(item) }}</span>
                        <v-btn
                          icon
                          x-small
                          @click="startEditingServiceCompletedDate(item)"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          x-small
                          :disabled="submitting"
                          @click="removeServiceCompletedDate(item)"
                        >
                          <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                      <v-menu
                        v-else-if="showServiceCompletedEditor(item)"
                        v-model="
                          serviceCompletedDateMenus[
                            serviceCompletedDateInputKey(item)
                          ]
                        "
                        :close-on-content-click="false"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-bind="attrs"
                            append-icon="mdi-calendar"
                            background-color="#0000000f"
                            dense
                            hide-details
                            :class="{
                              'p-service-date-required':
                                isServiceEtaRequiredForItem(item) &&
                                !item.supplierCompletedServiceDate
                            }"
                            outlined
                            readonly
                            :value="
                              $filters.formatDatePicker(
                                item.supplierCompletedServiceDate
                              )
                            "
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          :min="completedServiceDateMin"
                          :max="currentDate"
                          v-model="item.supplierCompletedServiceDate"
                          @change="onPickServiceCompletedDate(item)"
                        ></v-date-picker>
                      </v-menu>
                      <span v-else>{{
                        serviceCompletedDisplayValue(item)
                      }}</span>
                    </v-col>
                  </template>
                </template>
              </v-row>
            </v-card>
          </v-col>
        </template>

        <template v-else>
          <v-card
            class="rounded-2 mt-4"
            :class="{
              'p-order-details-purchase--chat-open':
                orderChatEnabled && chatOpen
            }"
          >
            <v-row no-gutters align="center" class="p-div-title pt-3 pb-3 px-4">
              <v-col cols="12" md="6">
                <v-chip
                  small
                  class="mr-2 mb-2 i-tag-warning"
                  v-if="isCancelled"
                  >{{ mutableOrder.status }}</v-chip
                >
                <v-chip
                  small
                  class="mr-2 mb-2 i-tag-primary"
                  v-else-if="isProcessedOffline"
                  >OFFLINE</v-chip
                >
                <span class="g-heading-m g-bold p-title mr-2">
                  Purchase Order Number
                  {{ `#${mutableOrder.supplierOrderNumber}` }}
                </span>
              </v-col>

              <v-col cols="12" md="6" class="text-right">
                <v-icon
                  color="primary"
                  :disabled="isCancelled || !mutableOrder.confirmedPdfAvailable"
                  v-tooltip="'Export Purchase Order PDF'"
                  @click="fetchPDF()"
                >
                  mdi-file-pdf-box
                </v-icon>
                <v-icon
                  v-if="!isServiceOrder"
                  class="ml-2"
                  color="primary"
                  :disabled="isCancelled || !mutableOrder.confirmedPdfAvailable"
                  v-tooltip="'Export Delivery Note PDF'"
                  @click="fetchPDF(true)"
                >
                  mdi-truck-check
                </v-icon>
                <v-icon
                  v-if="orderChatEnabled"
                  class="ml-2"
                  color="primary"
                  :disabled="
                    isCancelled ||
                    isProcessedOffline ||
                    !mutableOrder.invoiceNumber
                  "
                  v-tooltip="'Export Fees Invoice PDF'"
                  @click="fetchInvoicePDF()"
                >
                  mdi-receipt-text-check
                </v-icon>
                <v-badge
                  v-if="orderChatEnabled"
                  class="ml-2"
                  color="orange"
                  dot
                  overlap
                  :value="hasUnreadSupportMessages"
                >
                  <v-icon
                    :color="isDelivered ? 'grey' : 'secondary'"
                    v-tooltip="
                      isDelivered
                        ? 'Retailer messages - order delivered'
                        : chatOpen
                          ? 'Close Retailer support'
                          : 'Open Retailer support'
                    "
                    @click="toggleChat"
                  >
                    mdi-message-text-outline
                  </v-icon>
                </v-badge>
              </v-col>
            </v-row>

            <v-row class="px-4">
              <v-col class="mb-3" cols="12" md="6" lg="5">
                <v-simple-table>
                  <tr>
                    <td>Order Date & Time</td>
                    <td>
                      {{ $filters.formatDateTime12h(mutableOrder.createdAt) }}
                    </td>
                  </tr>
                  <tr v-if="mutableOrder.actualFinalConfirmationDate">
                    <td>PO Confirmed Date</td>
                    <td>
                      {{
                        $filters.formatDateTime12h(
                          mutableOrder.actualFinalConfirmationDate
                        )
                      }}
                    </td>
                  </tr>
                  <tr v-if="mutableOrder.summary">
                    <td>PO Total{{ incTaxSuffix }}</td>
                    <td>
                      {{
                        $filters.currency(
                          displayOrderTotal(mutableOrder.summary)
                        )
                      }}
                    </td>
                  </tr>
                  <tr v-if="hasBannerGroup">
                    <td>Banner Group</td>
                    <td>{{ mutableOrder.store.bannerGroupName }}</td>
                  </tr>
                  <tr>
                    <td>{{ isILR ? 'Customer' : 'Retailer' }}</td>
                    <td>
                      {{ showStoreName }}
                      <v-btn
                        v-if="mutableOrder.supplierOrderQueryId"
                        icon
                        v-tooltip="{
                          content: 'Order Query',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        :to="{
                          name: 'order-query-details',
                          params: { id: mutableOrder.supplierOrderQueryId }
                        }"
                      >
                        <v-icon class="i-order-query-link"
                          >mdi-clock-alert-outline</v-icon
                        >
                      </v-btn>
                    </td>
                  </tr>
                  <tr>
                    <td>Delivery Address</td>
                    <td>{{ showDeliveryAddress }}</td>
                  </tr>
                  <tr>
                    <td>Payment Type</td>
                    <td
                      :class="{
                        'font-weight-bold':
                          mutableOrder.paymentType === 'SUPPLIER_DIRECT'
                      }"
                    >
                      <i-payment-type
                        v-if="mutableOrder.paymentType"
                        :paymentType="mutableOrder.paymentType"
                        :textOnly="true"
                      />
                      <span
                        v-if="mutableOrder.paymentType === 'SUPPLIER_DIRECT'"
                        class="error--text text--darken-1"
                      >
                        <template v-if="mutableOrder.offlinePaymentDays">
                          ({{ mutableOrder.offlinePaymentDays }} days)
                        </template>
                        <template v-else> (Prepayment) </template>
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>Processed By</td>
                    <td class="text-break">{{ processedByDisplay }}</td>
                  </tr>
                </v-simple-table>
              </v-col>

              <v-col class="order-details pt-2 pb-4" cols="12">
                <div>
                  <strong>{{ isILR ? 'CUSTOMER' : 'RETAILER' }} MESSAGE</strong>
                </div>
                <div class="i-message">
                  <span>{{ retailerMessage }}</span>
                </div>
              </v-col>

              <v-col
                class="order-details pt-2 pb-4"
                cols="12"
                v-if="supplierMessages"
              >
                <div><strong>SUPPLIER MESSAGE(S)</strong></div>
                <div class="i-message">
                  <span
                    v-for="(supplierMessage, index) in supplierMessages"
                    :key="index"
                    >{{ supplierMessage }}<br
                  /></span>
                </div>
              </v-col>

              <template
                v-if="
                  !isServiceOrder &&
                  isDespatched &&
                  !isCancelled &&
                  !isProcessedOffline
                "
              >
                <v-col cols="12" class="order-details pb-0">
                  <div><strong>PROVIDED INFORMATION</strong></div>
                </v-col>

                <v-col cols="12" md="6" lg="3" v-if="deliveryDate">
                  <v-text-field
                    append-icon="mdi-calendar"
                    background-color="#0000000f"
                    dense
                    label="Estimated Delivery Date"
                    outlined
                    readonly
                    v-model="formattedDeliveryDate"
                  ></v-text-field>
                </v-col>

                <v-col v-if="isDelivered" cols="12" md="6" lg="3">
                  <v-text-field
                    append-icon="mdi-calendar"
                    background-color="#0000000f"
                    dense
                    label="Delivered Date"
                    outlined
                    readonly
                    v-model="formattedDeliveredDate"
                  ></v-text-field>
                </v-col>

                <v-col
                  v-if="mutableOrder.retailerAcceptedDate"
                  cols="12"
                  md="6"
                  lg="3"
                >
                  <v-text-field
                    append-icon="mdi-calendar"
                    background-color="#0000000f"
                    dense
                    :label="
                      tenantPaymentTransactionsConfig.purchaseOrderFinalisedDateField ===
                      'SUPPLIER_DELIVERED_ENTRY_DATE'
                        ? `${
                            isILR ? 'Customer' : 'Retailer'
                          } Actions Closed Date`
                        : `${isILR ? 'Customer' : 'Retailer'} Accepted Date`
                    "
                    outlined
                    readonly
                    v-model="formattedRetailerAcceptedDate"
                  ></v-text-field>
                </v-col>

                <v-col class="mt-md-n6 mt-lg-0 mb-n2" cols="12" md="6" lg="3">
                  <v-text-field
                    append-icon="mdi-map-marker"
                    background-color="#0000000f"
                    dense
                    label="Tracking Link"
                    outlined
                    readonly
                    :value="trackingLink || 'Not provided'"
                  ></v-text-field>
                </v-col>
              </template>
            </v-row>

            <v-divider></v-divider>

            <v-data-table
              :headers="headers"
              :height="tableHeight"
              :items="mutableOrder.supplierOrderItems"
              :items-per-page="-1"
              disable-sort
              hide-default-footer
              fixed-header
            >
              <template #[`header.variableWeightInKg`]="{ header }">
                {{ header.text }}
                <v-icon
                  class="i-tooltip-color pb-1"
                  small
                  v-tooltip="{ ref: 'variableWeightToolTipContent' }"
                >
                  mdi-help-circle-outline
                </v-icon>
                <span ref="variableWeightToolTipContent">
                  Applies to Variable Weight products only
                  <br />
                  Limited to +15% Revised Qty
                </span>
              </template>

              <template v-slot:[`item.total-price`]="{ item }">
                <span>{{ showItemPrice(item) }}</span>
              </template>

              <template v-slot:[`item.quantity`]="{ item }">
                <span>{{ $filters.text(item.quantity) }}</span>
              </template>

              <template v-slot:[`item.originalVariableWeightInKg`]="{ item }">
                <span v-if="item.originalVariableWeightInKg">{{
                  `${item.originalVariableWeightInKg} KG`
                }}</span>
                <span v-else>-</span>
              </template>

              <template v-slot:[`item.variableWeightInKg`]="{ item }">
                <span v-if="item.variableWeightInKg">{{
                  `${item.variableWeightInKg} KG`
                }}</span>
                <span v-else>-</span>
              </template>

              <template v-slot:[`item.retailerRequestedDate`]="{ item }">
                <span>{{
                  $filters.formatDate(item.retailerRequestedDate)
                }}</span>
              </template>

              <template v-slot:[`item.supplierEtaDate`]="{ item }">
                <v-menu
                  v-if="showServiceEtaEditor(item)"
                  v-model="serviceEtaMenus[serviceEtaInputKey(item)]"
                  :close-on-content-click="false"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      append-icon="mdi-calendar"
                      background-color="#0000000f"
                      class="my-1"
                      :class="{
                        'p-service-date-required':
                          isServiceEtaRequiredForItem(item) &&
                          !item.supplierEtaDate
                      }"
                      dense
                      hide-details
                      outlined
                      readonly
                      :value="$filters.formatDatePicker(item.supplierEtaDate)"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :min="currentDate"
                    v-model="item.supplierEtaDate"
                    @change="onPickServiceEtaDate(item)"
                  ></v-date-picker>
                </v-menu>
                <span v-else>{{ serviceEtaDisplayValue(item) }}</span>
              </template>

              <template v-slot:[`item.supplierCompletedServiceDate`]="{ item }">
                <div
                  v-if="showServiceCompletedActions(item)"
                  class="d-flex align-center justify-center ga-1"
                >
                  <span>{{ serviceCompletedDisplayValue(item) }}</span>
                  <v-btn
                    icon
                    x-small
                    @click="startEditingServiceCompletedDate(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :disabled="submitting"
                    @click="removeServiceCompletedDate(item)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
                <v-menu
                  v-else-if="showServiceCompletedEditor(item)"
                  v-model="
                    serviceCompletedDateMenus[
                      serviceCompletedDateInputKey(item)
                    ]
                  "
                  :close-on-content-click="false"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      append-icon="mdi-calendar"
                      background-color="#0000000f"
                      class="my-1"
                      :class="{
                        'p-service-date-required':
                          isServiceEtaRequiredForItem(item) &&
                          !item.supplierCompletedServiceDate
                      }"
                      dense
                      hide-details
                      outlined
                      readonly
                      :value="
                        $filters.formatDatePicker(
                          item.supplierCompletedServiceDate
                        )
                      "
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :min="completedServiceDateMin"
                    :max="currentDate"
                    v-model="item.supplierCompletedServiceDate"
                    @change="onPickServiceCompletedDate(item)"
                  ></v-date-picker>
                </v-menu>
                <span v-else>{{ serviceCompletedDisplayValue(item) }}</span>
              </template>
            </v-data-table>

            <v-divider class="my-2" v-if="!isDelivered"></v-divider>

            <v-row class="mx-1" v-if="!isDelivered">
              <v-spacer></v-spacer>
              <v-btn
                rounded
                text
                class="i-btn-text-red p-confirm-btn mt-2 mr-4 mb-4"
                @click="cancelConfirmationDialog = true"
                v-if="showCancelOrderButton"
              >
                Cancel Order
              </v-btn>
              <v-btn
                rounded
                class="i-btn-block-orange p-confirm-btn mt-2 mr-4 mb-4"
                :disabled="isServiceOrder && !allServiceEtaDatesFilled"
                @click="deliveryDialog = true"
                v-if="showConfirmDespatchButton"
              >
                Confirm Despatch
              </v-btn>
              <v-btn
                rounded
                class="i-btn-block-orange p-confirm-btn mt-2 mr-4 mb-4"
                :disabled="!allServiceEtaDatesFilled"
                :loading="submitting"
                @click="saveServiceEtaDates()"
                v-if="showSaveServiceEtaButton"
              >
                Save ETA Date
              </v-btn>
              <v-btn
                rounded
                class="i-btn-block-orange p-confirm-btn mt-2 mr-4 mb-4"
                :loading="checkingUnreadDeliveryMessages"
                @click="openDeliveredDialog"
                v-if="isDespatched && !isServiceOrder"
              >
                Confirm Delivery
              </v-btn>
              <v-btn
                rounded
                class="i-btn-block-orange p-confirm-btn mt-2 mr-4 mb-4"
                :disabled="!hasAnyServiceCompletedDatesFilled"
                :loading="submitting"
                @click="saveCompletedServiceDates()"
                v-if="showSaveCompletedServiceDateButton"
              >
                Save Completed Service Date
              </v-btn>
              <v-btn
                rounded
                class="i-btn-block-orange p-confirm-btn mt-2 mr-4 mb-4"
                :disabled="!allServiceCompletedDatesFilled"
                :loading="submitting"
                @click="confirmServicesComplete()"
                v-if="showConfirmServicesCompleteButton"
              >
                Confirm Services Complete
              </v-btn>
            </v-row>

            <aside v-if="orderChatEnabled && chatOpen" class="p-order-chat">
              <v-toolbar flat color="primary" dark class="p-order-chat__header">
                <v-avatar size="32" class="mr-3" color="secondary">
                  <v-icon small>mdi-store-outline</v-icon>
                </v-avatar>
                <div>
                  <v-toolbar-title>Retailer support</v-toolbar-title>
                  <div class="p-order-chat__order">
                    PO {{ mutableOrder.supplierOrderNumber }}
                  </div>
                </div>
                <v-spacer />
                <v-btn
                  icon
                  aria-label="Close Retailer support"
                  @click="chatOpen = false"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>
              <div
                v-if="showChatRetentionMessage"
                class="p-order-chat__meta px-4 py-3"
              >
                <v-icon small color="secondary" class="mr-2"
                  >mdi-clock-outline</v-icon
                >
                {{ chatRetentionMessage }}
              </div>
              <div
                v-if="chatDisabledMessage"
                class="p-order-chat__alert px-4 py-3"
              >
                <v-icon small class="mr-2">mdi-alert-circle-outline</v-icon>
                {{ chatDisabledMessage }}
              </div>
              <v-divider />
              <div ref="chatMessages" class="p-order-chat__messages px-4 py-4">
                <div
                  v-for="message in conversation.messages"
                  :key="message.id"
                  class="p-order-chat__message mb-4"
                  :class="{
                    'p-order-chat__message--own':
                      message.senderType === 'SUPPLIER'
                  }"
                >
                  <div class="p-order-chat__message-meta mb-1">
                    <strong>{{ message.senderName }}</strong>
                    <span class="p-order-chat__timestamp">{{
                      formatChatDateTime(message.createdAt)
                    }}</span>
                  </div>
                  <div class="p-order-chat__body">{{ message.body }}</div>
                </div>
                <div
                  v-if="!conversation.messages.length"
                  class="p-order-chat__empty text-center py-8"
                >
                  <v-icon color="secondary" size="34"
                    >mdi-message-text-outline</v-icon
                  >
                  <div class="mt-3">
                    Start a conversation about this purchase order.
                  </div>
                </div>
              </div>
              <v-divider />
              <div class="px-4 pt-4 pb-2">
                <v-textarea
                  v-model="newMessage"
                  label="Message retailer support"
                  outlined
                  rows="3"
                  auto-grow
                  :counter="4000"
                  :disabled="chatReadOnly"
                  hide-details="auto"
                />
              </div>
              <div class="d-flex justify-end px-4 pb-4">
                <v-btn
                  class="i-btn-block-secondary"
                  :disabled="chatReadOnly || !newMessage.trim()"
                  @click="sendChatMessage"
                >
                  Send message
                  <v-icon right>mdi-send</v-icon>
                </v-btn>
              </div>
            </aside>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import Bugsnag from '@bugsnag/js'
import { mapActions, mapGetters } from 'vuex'
import {
  fetchSupplierOrderInvoicePdfUrl,
  fetchOrderPDF,
  submitOrder,
  updateServiceCompletedDates,
  cancelOrder
} from '@/services/supplierOrders'
import iBreadcrumbs from '@/components/Breadcrumbs'
import moment from 'moment-timezone'
import {
  openFile,
  dateToISOStringIncludingTimezone,
  isValidUrl
} from '@/utils/common'
import iPaymentType from '@/components/PaymentType'
import {
  fetchOrderConversation,
  fetchOrderConversationHistory,
  sendOrderConversationMessage
} from '@/services/orderConversations'
import { FTWebSocket } from '@/utils/FTWebSocket'
import { formatUtcDateTime } from '@/utils/timezone'
import {
  pickSupplierAmount,
  pickSupplierCostTotal
} from '@/utils/supplierAmountDisplay'

const FIELD_RULES = {
  trackingLinkRules: [
    (v) =>
      !v ||
      isValidUrl(v) ||
      'Must be a valid URL (e.g. https://my-tracking-link.com/)'
  ]
}

const NUM_DECIMALS_FOR_VW_KG = 2

const ROUTE_RETURN = {
  DELIVERED: { name: 'reports' },
  DESPATCHED: { name: 'purchase-orders', query: { status: 'DURING_DELIVERY' } },
  IN_TRANSIT: { name: 'purchase-orders', query: { status: 'BEFORE_DELIVERY' } }
}

export default {
  props: ['orderDetails'],

  components: {
    iBreadcrumbs,
    iPaymentType
  },

  data() {
    return {
      currentDate: moment().format('YYYY-MM-DD'),
      cancelConfirmationDialog: false,
      cancelling: false,
      deliveredDate: null,
      deliveredDialog: false,
      deliveredDateMenu: false,
      deliveryDate: null,
      deliveryDialog: false,
      deliveryDateMenu: false,
      fetchingInvoicePDF: false,
      proofOfDelivery: false,
      serviceCompletedEditingRows: {},
      serviceCompletedDateMenus: {},
      serviceEtaMenus: {},
      submitting: false,
      reasonForCancellation: null,
      trackingLink: null,
      mutableOrder: {},
      chatOpen: true,
      hasUnreadSupportMessages: false,
      conversation: { messages: [] },
      deliveryChatReviewed: false,
      unreadDeliveryChatDialog: false,
      checkingUnreadDeliveryMessages: false,
      unreadDeliveryMessageCount: 0,
      newMessage: '',
      ...FIELD_RULES
    }
  },

  computed: {
    processedByDisplay() {
      const name = this.mutableOrder?.confirmedByName?.trim()
      const email = this.mutableOrder?.confirmedByEmail?.trim()
      return name && email
        ? `${name} (${email})`
        : name || email || 'Not recorded'
    },

    ...mapGetters('tenant', [
      'isILR',
      'orderChatEnabled',
      'supportEmail',
      'tenantPaymentTransactionsConfig',
      'tenantCountryRegion'
    ]),

    headers() {
      const baseHeaders = [
        {
          text: 'SUPPLIER ITEM CODE',
          value: 'supplierProductCode',
          align: 'start'
        },
        {
          text: 'PRODUCT DESCRIPTION',
          value: 'productDescription',
          align: 'start'
        },
        ...(!this.isServiceOrder
          ? [{ text: 'CARTON GTIN', value: 'gtin', align: 'center' }]
          : []),
        {
          text: this.tenantCountryRegion === 'US' ? 'TOTAL' : 'TOTAL (INC TAX)',
          value: 'total-price',
          align: 'center'
        },
        { text: 'ORDERED QTY', value: 'originalQuantity', align: 'center' },
        { text: 'DELIVERED QTY', value: 'quantity', align: 'center' }
      ]

      const serviceHeaders = [
        {
          text: 'REQUESTED DATE',
          value: 'retailerRequestedDate',
          align: 'center'
        },
        {
          text: 'ETA DATE',
          value: 'supplierEtaDate',
          align: 'center',
          width: '170'
        }
      ]

      const completedServiceHeader = {
        text: 'COMPLETED SERVICE DATE',
        value: 'supplierCompletedServiceDate',
        align: 'center',
        width: '190'
      }

      const weightHeaders = [
        {
          text: 'ORDERED WEIGHT (KG)',
          value: 'originalVariableWeightInKg',
          align: 'center',
          hidden: this.isILR
        },
        {
          text: 'DELIVERED WEIGHT (KG)',
          value: 'variableWeightInKg',
          align: 'center',
          width: '150',
          hidden: this.isILR
        }
      ]

      return [
        ...baseHeaders,
        ...(this.isServiceOrder
          ? [
              ...serviceHeaders,
              ...(this.isDespatched ? [completedServiceHeader] : [])
            ]
          : [...weightHeaders])
      ].filter((i) => !i.hidden)
    },

    computedBreadcrumbs() {
      let breadcrumbs = []
      if (this.isDelivered) {
        breadcrumbs = [
          { text: 'Home', exact: true, to: { name: 'dashboard' } },
          { text: 'Reports', exact: true, to: { name: 'reports' } },
          {
            text: 'Purchase Orders History',
            exact: true,
            to: { name: 'purchase-orders-history' }
          },
          { text: this.$route.meta.title, to: null }
        ]
      } else if (this.isDespatched) {
        breadcrumbs = [
          { text: 'Home', exact: true, to: { name: 'dashboard' } },
          {
            text: 'Purchase Orders',
            exact: true,
            to: {
              name: 'purchase-orders',
              query: { status: 'DURING_DELIVERY' }
            }
          },
          { text: this.$route.meta.title, to: null }
        ]
      } else if (this.isInTransit) {
        breadcrumbs = [
          { text: 'Home', exact: true, to: { name: 'dashboard' } },
          {
            text: 'Purchase Orders',
            exact: true,
            to: {
              name: 'purchase-orders',
              query: { status: 'BEFORE_DELIVERY' }
            }
          },
          { text: this.$route.meta.title, to: null }
        ]
      }
      return breadcrumbs
    },

    returnRoute() {
      if (this.isDelivered) return ROUTE_RETURN.DELIVERED
      if (this.isDespatched) return ROUTE_RETURN.DESPATCHED
      else return ROUTE_RETURN.IN_TRANSIT
    },

    formattedDeliveredDate() {
      return this.$options.filters.formatDatePicker(this.deliveredDate)
    },

    formattedDeliveryDate() {
      return this.$options.filters.formatDatePicker(this.deliveryDate)
    },

    formattedRetailerAcceptedDate() {
      return this.$options.filters.formatDatePicker(
        this.mutableOrder.retailerAcceptedDate
      )
    },

    hasDeliveredDate() {
      return !!this.deliveredDate
    },

    hasDeliveryDate() {
      return !!this.deliveryDate
    },

    hasBannerGroup() {
      return !!this.mutableOrder.store?.bannerGroupName
    },

    hasTrackingLink() {
      return !!this.mutableOrder.trackingLink
    },

    isDelivered() {
      return this.isDespatched && !!this.mutableOrder.deliveredDate
    },

    isDespatched() {
      return !!this.mutableOrder.despatchConfirmed
    },

    isServiceEtaReadOnly() {
      return this.isDespatched || this.isDelivered
    },

    completedServiceDateMin() {
      const orderDate =
        this.mutableOrder?.createdAt ||
        this.mutableOrder?.createdDate ||
        this.mutableOrder?.orderDate
      const parsedOrderDate = moment(orderDate)
      return parsedOrderDate.isValid()
        ? parsedOrderDate.format('YYYY-MM-DD')
        : this.currentDate
    },

    showConfirmDespatchButton() {
      return !this.isDespatched && !this.isServiceOrder
    },

    showSaveServiceEtaButton() {
      return this.isServiceOrder && !this.isDespatched && !this.isDelivered
    },

    showCancelOrderButton() {
      return this.isServiceOrder && this.isDraft && !this.isCancelled
    },

    showSaveCompletedServiceDateButton() {
      return (
        this.isServiceOrder &&
        this.isDespatched &&
        !this.isDelivered &&
        this.serviceOrderItemCount > 1 &&
        !this.allServiceCompletedDatesFilled
      )
    },

    allServiceCompletedDatesFilled() {
      if (!this.isServiceOrder) return true
      const items = this.mutableOrder?.supplierOrderItems || []
      return (
        items.length > 0 &&
        items.every(
          (item) =>
            !this.isServiceEtaRequiredForItem(item) ||
            this.isValidCompletedServiceDate(item.supplierCompletedServiceDate)
        )
      )
    },

    serviceOrderItemCount() {
      if (!this.isServiceOrder) return 0
      const items = this.mutableOrder?.supplierOrderItems || []
      return items.filter((item) => this.isServiceEtaRequiredForItem(item))
        .length
    },

    filledServiceCompletedDatesCount() {
      if (!this.isServiceOrder) return 0
      const items = this.mutableOrder?.supplierOrderItems || []
      return items.filter(
        (item) =>
          this.isServiceEtaRequiredForItem(item) &&
          this.isValidCompletedServiceDate(item.supplierCompletedServiceDate)
      ).length
    },

    hasAnyServiceCompletedDatesFilled() {
      return this.filledServiceCompletedDatesCount > 0
    },

    showConfirmServicesCompleteButton() {
      return this.isServiceOrder && this.isDespatched && !this.isDelivered
    },

    isCancelled() {
      return this.mutableOrder.status === 'CANCELLED'
    },

    isDraft() {
      return this.mutableOrder.status === 'DRAFT'
    },

    isInTransit() {
      return this.mutableOrder.deliveryStatus === 'OUTSTANDING'
    },

    isProcessedOffline() {
      return this.mutableOrder.status === 'PROCESSED_OFFLINE'
    },

    retailerMessage() {
      return this.orderDetails?.orderNotes[0]?.message || 'None'
    },

    supplierMessages() {
      if (!this.orderDetails?.supplierOrderNotes?.length) return
      return this.orderDetails?.supplierOrderNotes.map((m) => m.message)
    },

    showDeliveryAddress() {
      return this.mutableOrder.deliveryAddress !== undefined
        ? this.mutableOrder.deliveryAddress.line1 +
            ', ' +
            this.mutableOrder.deliveryAddress.suburb +
            ', ' +
            this.mutableOrder.deliveryAddress.state +
            ' ' +
            this.mutableOrder.deliveryAddress.postCode
        : 'Not available'
    },

    showStoreName() {
      return this.mutableOrder.store?.name !== undefined
        ? this.mutableOrder.store?.name
        : 'Not available'
    },

    tableHeight() {
      const DEFAULT_ROW_HEIGHT = 48 // Default Vuetify height
      const MAX_ROWS_VISIBLE = 5 // Adjust based on view if needed
      return this.mutableOrder.supplierOrderItems?.length > MAX_ROWS_VISIBLE
        ? DEFAULT_ROW_HEIGHT * (MAX_ROWS_VISIBLE + 1) // +1 as header is also included
        : undefined
    },

    validDespatchInfo() {
      return (
        (this.isServiceOrder || !!this.deliveryDate) &&
        (!this.isServiceOrder || this.allServiceEtaDatesFilled) &&
        (!this.trackingLink || isValidUrl(this.trackingLink))
      )
    },

    allServiceEtaDatesFilled() {
      if (!this.isServiceOrder) return true
      const items = this.mutableOrder?.supplierOrderItems || []
      return (
        items.length > 0 &&
        items.every(
          (item) =>
            !this.isServiceEtaRequiredForItem(item) || !!item.supplierEtaDate
        )
      )
    },

    isServiceOrder() {
      const items = this.mutableOrder?.supplierOrderItems
      if (!items || !items.length) return false

      return items.every(
        (item) => item.productType === 'SERVICE' || !!item.retailerRequestedDate
      )
    },

    incTaxSuffix() {
      return this.tenantCountryRegion === 'US' ? '' : ' (inc Tax)'
    },

    incTaxSuffixUpper() {
      return this.tenantCountryRegion === 'US' ? '' : ' (INC TAX)'
    },

    chatReadOnly() {
      return this.isDelivered || !!this.chatDisabledReason
    },

    hasUnreadDeliveryMessages() {
      return (
        this.orderChatEnabled &&
        (this.unreadDeliveryMessageCount > 0 ||
          this.currentConversationNeedsDeliveryReview)
      )
    },

    currentConversationNeedsDeliveryReview() {
      const lastMessage =
        this.conversation.messages[this.conversation.messages.length - 1]
      return (
        !this.deliveryChatReviewed && lastMessage?.senderType === 'RETAILER'
      )
    },

    chatRetentionMessage() {
      if (this.chatDisabledReason) {
        return 'Message history remains available, but replies are currently disabled.'
      }
      return this.chatReadOnly
        ? 'Messages are not available after delivery is confirmed. Retailers can raise an Order Query or Credit Return for any issues.'
        : 'Messages are available until delivery is confirmed.'
    },

    showChatRetentionMessage() {
      return !!this.chatDisabledReason || this.chatReadOnly
    },

    chatDisabledReason() {
      return this.conversation.messageDisabledReason
    },

    supportContact() {
      return this.supportEmail || 'support'
    },

    chatDisabledMessage() {
      if (this.chatDisabledReason === 'SUPPLIER_SUSPENDED') {
        return `Your supplier account is suspended and messages cannot be sent. Please contact support at ${this.supportContact}.`
      }
      if (this.chatDisabledReason === 'RETAILER_CREDIT_HOLD') {
        return `This retailer is on credit hold and messages cannot be sent. Please contact support at ${this.supportContact}.`
      }
      return ''
    }
  },

  methods: {
    ...mapActions('notifications', [
      'addNotification',
      'fetchActionableSupportMessages'
    ]),

    toggleChat() {
      if (!this.orderChatEnabled) return
      this.chatOpen = !this.chatOpen
      if (this.chatOpen) {
        this.markConversationRead()
        this.fetchConversation()
      }
    },

    fetchConversation() {
      return fetchOrderConversation(
        this.orderDetails.id,
        this.orderDetails.supplierId
      ).then((conversation) => {
        this.conversation = conversation
        this.markConversationRead()
        this.fetchActionableSupportMessages(this.orderDetails.supplierId)
        this.scrollChatToLatestMessage()
      })
    },

    markConversationRead() {
      this.hasUnreadSupportMessages = false
      this.unreadDeliveryMessageCount = 0
    },

    sendChatMessage() {
      if (!this.orderChatEnabled || this.chatReadOnly) return
      sendOrderConversationMessage(
        this.orderDetails.id,
        this.orderDetails.supplierId,
        this.newMessage
      ).then((conversation) => {
        this.conversation = conversation
        this.newMessage = ''
        this.deliveryChatReviewed = true
        this.scrollChatToLatestMessage()
      })
    },

    formatChatDateTime(value) {
      return formatUtcDateTime(value, 'DD/MM/YYYY h:mm a', '-')
    },

    openDeliveredDialog() {
      if (!this.orderChatEnabled) {
        this.deliveredDialog = true
        return
      }

      this.checkingUnreadDeliveryMessages = true
      return this.refreshDeliveryUnreadMessages()
        .then(() => {
          if (this.hasUnreadDeliveryMessages) {
            this.unreadDeliveryChatDialog = true
            return
          }

          this.deliveredDialog = true
        })
        .catch((err) => {
          Bugsnag.notify(err)
          this.addNotification({
            color: 'error',
            text: 'Could not check unread messages, please try again...'
          })
        })
        .finally(() => {
          this.checkingUnreadDeliveryMessages = false
        })
    },

    refreshDeliveryUnreadMessages() {
      return fetchOrderConversationHistory(this.orderDetails.supplierId).then(
        (conversations) => {
          const conversation = conversations.find(
            (item) => item.supplierOrderId === Number(this.orderDetails.id)
          )
          this.unreadDeliveryMessageCount = conversation?.unreadCount || 0
        }
      )
    },

    readUnreadDeliveryMessage() {
      this.unreadDeliveryChatDialog = false
      this.chatOpen = true
      this.markConversationRead()
      return this.fetchConversation().then(() => {
        this.deliveryChatReviewed = true
      })
    },

    scrollChatToLatestMessage() {
      this.$nextTick(() => {
        const messages = this.$refs.chatMessages
        if (messages) messages.scrollTop = messages.scrollHeight
      })
    },

    checkActionRequired() {
      if (this.isServiceOrder) return

      this.deliveryDialog = !this.isDespatched
      this.deliveredDialog = this.isDespatched && !this.isDelivered
    },

    dateToISOStringIncludingTimezone(date) {
      return dateToISOStringIncludingTimezone(date)
    },

    deliver() {
      if (this.orderChatEnabled) {
        this.checkingUnreadDeliveryMessages = true
        return this.refreshDeliveryUnreadMessages()
          .then(() => {
            if (this.hasUnreadDeliveryMessages) {
              this.unreadDeliveryChatDialog = true
              return
            }

            this.submitDelivery()
          })
          .catch((err) => {
            Bugsnag.notify(err)
            this.addNotification({
              color: 'error',
              text: 'Could not check unread messages, please try again...'
            })
          })
          .finally(() => {
            this.checkingUnreadDeliveryMessages = false
          })
      }

      return this.submitDelivery()
    },

    submitDelivery() {
      const deliveredDate = this.deliveredDate
        ? moment(this.deliveredDate).format()
        : null

      const request = {
        trackingLink: this.trackingLink,
        proofOfDelivery: this.proofOfDelivery,
        deliveredDate
      }

      this.submit(request)
    },

    serviceEtaInputKey(item) {
      return `service-eta-${item.id || item.productCode}`
    },

    serviceItemQuantity(item) {
      return Number(
        item?.quantity ?? item?.revisedQuantity ?? item?.originalQuantity ?? 0
      )
    },

    isServiceEtaRequiredForItem(item) {
      return this.serviceItemQuantity(item) > 0
    },

    serviceCompletedDateInputKey(item) {
      return `service-completed-${item.id || item.productCode}`
    },

    onPickServiceEtaDate(item) {
      this.$set(this.serviceEtaMenus, this.serviceEtaInputKey(item), false)
      this.$set(item, 'supplierEtaDate', item.supplierEtaDate)
    },

    onPickServiceCompletedDate(item) {
      this.$set(
        this.serviceCompletedDateMenus,
        this.serviceCompletedDateInputKey(item),
        false
      )
      this.$set(
        item,
        'supplierCompletedServiceDate',
        item.supplierCompletedServiceDate
      )
      if (item._savedSupplierCompletedServiceDate) {
        this.updateServiceCompletedDate(item)
      }
    },

    isValidCompletedServiceDate(date) {
      if (!date) return false
      const completedDate = moment(date)
      if (!completedDate.isValid()) return false
      return completedDate.isBetween(
        this.completedServiceDateMin,
        this.currentDate,
        'day',
        '[]'
      )
    },

    buildServiceEtaItems() {
      return this.mutableOrder.supplierOrderItems.map((item) => ({
        id: item.id,
        productCode: item.productCode,
        quantity: this.serviceItemQuantity(item),
        variableWeightInKg: item.variableWeightInKg,
        supplierEtaDate: this.isServiceEtaRequiredForItem(item)
          ? item.supplierEtaDate
          : null
      }))
    },

    buildServiceCompletedItems() {
      return this.mutableOrder.supplierOrderItems.map((item) => ({
        id: item.id,
        productCode: item.productCode,
        quantity: this.serviceItemQuantity(item),
        variableWeightInKg: item.variableWeightInKg,
        supplierEtaDate: item.supplierEtaDate,
        supplierCompletedServiceDate: this.isServiceEtaRequiredForItem(item)
          ? item.supplierCompletedServiceDate
          : null
      }))
    },

    buildServiceCompletedUpdateItems() {
      return this.mutableOrder.supplierOrderItems.map((item) => ({
        id: item.id,
        productCode: item.productCode,
        supplierCompletedServiceDate: this.isServiceEtaRequiredForItem(item)
          ? item.supplierCompletedServiceDate || null
          : null
      }))
    },

    lastServiceEtaDate() {
      return _.max(
        this.mutableOrder.supplierOrderItems
          .filter((item) => this.isServiceEtaRequiredForItem(item))
          .map((item) => item.supplierEtaDate)
          .filter(Boolean)
      )
    },

    lastServiceCompletedDate() {
      return _.max(
        this.mutableOrder.supplierOrderItems
          .filter((item) => this.isServiceEtaRequiredForItem(item))
          .map((item) => item.supplierCompletedServiceDate)
          .filter(Boolean)
      )
    },

    serviceEtaDisplayValue(itemOrDate) {
      if (
        itemOrDate &&
        typeof itemOrDate === 'object' &&
        !this.isServiceEtaRequiredForItem(itemOrDate)
      ) {
        return 'Not required'
      }
      const date =
        itemOrDate && typeof itemOrDate === 'object'
          ? itemOrDate.supplierEtaDate
          : itemOrDate
      return date ? this.$options.filters.formatDate(date) : 'Not provided'
    },

    serviceCompletedDisplayValue(itemOrDate) {
      if (
        itemOrDate &&
        typeof itemOrDate === 'object' &&
        !this.isServiceEtaRequiredForItem(itemOrDate)
      ) {
        return 'Not required'
      }
      const date =
        itemOrDate && typeof itemOrDate === 'object'
          ? itemOrDate.supplierCompletedServiceDate
          : itemOrDate
      return date ? this.$options.filters.formatDate(date) : 'Not provided'
    },

    showServiceEtaEditor(item) {
      return (
        this.isServiceEtaRequiredForItem(item) &&
        !this.isDespatched &&
        !this.isDelivered
      )
    },

    showServiceCompletedActions(item) {
      return (
        this.isServiceEtaRequiredForItem(item) &&
        !this.isDelivered &&
        !!item._savedSupplierCompletedServiceDate &&
        !this.serviceCompletedEditingRows[
          this.serviceCompletedDateInputKey(item)
        ]
      )
    },

    showServiceCompletedEditor(item) {
      return (
        this.isServiceEtaRequiredForItem(item) &&
        !this.isDelivered &&
        (!item._savedSupplierCompletedServiceDate ||
          !!this.serviceCompletedEditingRows[
            this.serviceCompletedDateInputKey(item)
          ])
      )
    },

    startEditingServiceCompletedDate(item) {
      if (this.isDelivered) return
      this.$set(
        this.serviceCompletedEditingRows,
        this.serviceCompletedDateInputKey(item),
        true
      )
      this.$set(
        this.serviceCompletedDateMenus,
        this.serviceCompletedDateInputKey(item),
        true
      )
    },

    syncServiceCompletedState() {
      this.mutableOrder.supplierOrderItems.forEach((orderItem) => {
        this.$set(
          orderItem,
          '_savedSupplierCompletedServiceDate',
          orderItem.supplierCompletedServiceDate
        )
      })
    },

    updateServiceCompletedDate(item) {
      const itemKey = this.serviceCompletedDateInputKey(item)
      const previousCompletedDate =
        item._savedSupplierCompletedServiceDate || null
      this.submitting = true

      updateServiceCompletedDates(this.mutableOrder.id, {
        items: this.buildServiceCompletedUpdateItems()
      })
        .then(() => {
          this.syncServiceCompletedState()
          this.$set(this.serviceCompletedEditingRows, itemKey, false)
          this.addNotification({
            color: 'secondary',
            text: 'Completed service date successfully updated!'
          })
        })
        .catch(() => {
          this.$set(item, 'supplierCompletedServiceDate', previousCompletedDate)
          this.addNotification({
            color: 'error',
            text: 'Completed service date could not be updated, try again...'
          })
        })
        .finally(() => {
          this.$set(this.serviceCompletedDateMenus, itemKey, false)
          this.submitting = false
        })
    },

    removeServiceCompletedDate(item) {
      const itemKey = this.serviceCompletedDateInputKey(item)
      const previousCompletedDate = item.supplierCompletedServiceDate
      this.$set(item, 'supplierCompletedServiceDate', null)
      this.$set(this.serviceCompletedEditingRows, itemKey, false)
      this.submitting = true

      updateServiceCompletedDates(this.mutableOrder.id, {
        items: this.buildServiceCompletedUpdateItems()
      })
        .then(() => {
          this.syncServiceCompletedState()
          this.addNotification({
            color: 'secondary',
            text: 'Completed service date successfully removed!'
          })
        })
        .catch(() => {
          this.$set(item, 'supplierCompletedServiceDate', previousCompletedDate)
          this.addNotification({
            color: 'error',
            text: 'Completed service date could not be removed, try again...'
          })
        })
        .finally(() => {
          this.$set(this.serviceCompletedDateMenus, itemKey, false)
          this.submitting = false
        })
    },

    saveServiceEtaDates() {
      const deliveryDate = this.lastServiceEtaDate()

      this.submit({
        confirmedDeliveryDate: deliveryDate
          ? moment(deliveryDate).format()
          : null,
        despatchConfirmed: true,
        items: this.buildServiceEtaItems()
      })
    },

    saveCompletedServiceDates() {
      this.submit({
        items: this.buildServiceCompletedItems()
      })
    },

    confirmServicesComplete() {
      const completedDate = this.lastServiceCompletedDate()

      this.submit({
        proofOfDelivery: true,
        deliveredDate: completedDate ? moment(completedDate).format() : null,
        items: this.buildServiceCompletedItems()
      })
    },

    despatch() {
      const deliveryDate = this.deliveryDate
        ? moment(this.deliveryDate).format()
        : null

      const request = {
        confirmedDeliveryDate: deliveryDate,
        trackingLink: this.trackingLink || null,
        despatchConfirmed: true,
        items: this.isServiceOrder ? this.buildServiceEtaItems() : null
      }

      this.submit(request)
    },

    fetchPDF(getDeliveryNote) {
      fetchOrderPDF(this.mutableOrder.id, getDeliveryNote)
        .then((response) => {
          const file = new Blob([response], { type: 'application/pdf' })
          const fileURL = URL.createObjectURL(file)
          openFile(fileURL)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Invoice ${this.mutableOrder.id} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
    },

    fetchInvoicePDF() {
      const orderId = this.mutableOrder.id
      this.fetchingInvoicePDF = true
      fetchSupplierOrderInvoicePdfUrl(orderId)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Order ${orderId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingInvoicePDF = false
        })
    },

    cancelOrder() {
      this.cancelling = true

      const request = {
        reasonForCancellation: this.reasonForCancellation
      }

      cancelOrder(this.mutableOrder.id, request)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Order successfully cancelled!'
          })
          this.$router.push({ name: 'purchase-orders' })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Order could not be cancelled'
          })
        })
        .finally(() => {
          this.cancelling = false
          this.cancelConfirmationDialog = false
        })
    },

    loadOrderInfo() {
      this.deliveryDate = this.mutableOrder.confirmedDeliveryDate
      this.deliveredDate = this.mutableOrder.deliveredDate
      this.proofOfDelivery = this.mutableOrder.proofOfDelivery
      if (this.isDespatched) this.trackingLink = this.mutableOrder.trackingLink
    },

    mapOrder(order) {
      const clonedOrder = _.cloneDeep(order)

      clonedOrder.supplierOrderItems = clonedOrder.supplierOrderItems.map(
        (item) => {
          if (item.variableWeightInKg) {
            item.revisedVariableWeightInKg = this.maxNumberDecimals(
              item.quantity * item.variableWeightTargetInKg,
              NUM_DECIMALS_FOR_VW_KG
            )

            item.variableWeightInKg = this.maxNumberDecimals(
              item.variableWeightInKg,
              NUM_DECIMALS_FOR_VW_KG
            )
          }

          return {
            ...item,
            revisedQuantity: item.quantity,
            _savedSupplierEtaDate: item.supplierEtaDate,
            _savedSupplierCompletedServiceDate:
              item.supplierCompletedServiceDate,
            weightType:
              item.variableWeightInKg >= 0 ? 'VARIABLE_WEIGHT' : 'FIXED_WEIGHT'
          }
        }
      )

      return clonedOrder
    },

    maxNumberDecimals(value, numberDecimals) {
      if (value < 0) return 0
      return Number(value).toFixed(numberDecimals)
    },

    showItemPrice(item) {
      return this.$options.filters.currency(
        pickSupplierAmount({
          tenantCountryRegion: this.tenantCountryRegion,
          exTax: item.summary?.totalExTax,
          incTax: item.summary?.totalIncTax
        })
      )
    },

    displayOrderTotal(summary) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: summary?.remittanceTotal
      })
    },

    showItemQuantity(quantity) {
      return quantity !== undefined ? quantity : 'Not available'
    },

    submit(request) {
      this.submitting = true
      submitOrder(this.mutableOrder.id, request)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Order successfully updated!'
          })
          this.$router.push(this.returnRoute)
        })
        .catch((err) => {
          let error
          switch (err.response?.data.code || 'UNKNOWN_ERROR') {
            case 'INVALID_TRACKING_LINK':
              error = err.response?.data.error || 'Invalid tracking link...'
              break

            default:
              Bugsnag.notify(err)
              error = 'Order could not be submitted, please try again...'
          }

          this.addNotification({ color: 'error', text: error })
        })
        .finally(() => {
          this.submitting = false
        })
    }
  },

  mounted() {
    this.mutableOrder = this.mapOrder(this.orderDetails)
    this.loadOrderInfo()
    this.checkActionRequired()
    if (this.orderChatEnabled) this.fetchConversation()
    FTWebSocket.subscribe('/user/queue/order-chat/message', (event) => {
      if (
        this.orderChatEnabled &&
        this.chatOpen &&
        event.supplierOrderId === Number(this.orderDetails.id)
      )
        this.fetchConversation()
      if (this.orderChatEnabled) {
        if (
          !this.chatOpen &&
          event.supplierOrderId === Number(this.orderDetails.id)
        ) {
          this.hasUnreadSupportMessages = true
        }
        this.addNotification({
          color: 'info',
          text: `New Retailer support message from ${event.senderName}`
        })
      }
    })
  }
}
</script>

<style lang="scss">
.theme--light {
  &.v-label--is-disabled {
    color: rgba(0, 0, 0, 0.55);
  }
  &.v-input--is-disabled {
    input,
    textarea {
      color: rgba(0, 0, 0, 0.55);
    }
  }
}
</style>

<style lang="scss" scoped>
#order-details-purchase {
  .p-order-details-purchase--chat-open {
    position: relative;
    padding-right: 400px;
  }

  .p-order-chat {
    background: white;
    border-left: 1px solid currentColor;
    bottom: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 380px;

    &__order,
    &__meta,
    &__timestamp {
      font-size: 0.75rem;
    }

    &__header {
      min-height: 72px;
    }

    &__meta {
      align-items: center;
      display: flex;
      opacity: 0.78;
    }

    &__alert {
      align-items: center;
      background: #fff7e6;
      color: #5d3d00;
      display: flex;
    }

    &__messages {
      max-height: 520px;
      min-height: 280px;
      overflow-y: auto;
    }

    &__message {
      max-width: 88%;
    }

    &__message--own {
      margin-left: auto;
    }

    &__message-meta {
      display: flex;
      gap: 8px;
      justify-content: space-between;
      opacity: 0.72;
    }

    &__body {
      background: rgba(0, 0, 0, 0.06);
      border-radius: 8px;
      padding: 12px;
      white-space: pre-wrap;
    }

    &__message--own &__body {
      background: $primary-color;
      color: white;
    }

    &__empty {
      opacity: 0.72;
    }
  }

  .p-div-title {
    background-color: $primary-color-light;
    color: $primary-color;
  }
  .p-title {
    display: inline-block;
    letter-spacing: 0.75px;
  }
  .p-service-date-required :deep(.v-input__slot) {
    border-color: $secondary-color;
    box-shadow: 0 0 0 1px rgb(var(--v-theme-secondary) / 30%);
  }
}
</style>
